import React, { useEffect, useState, useCallback } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { api } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, Users, Target, Trophy, ArrowUp, Pause, BarChart3, AlertTriangle } from 'lucide-react';

function MetricCard({ label, value, subtitle, icon: Icon, accent }) {
  const colors = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    red: 'bg-red-50 border-red-200 text-red-700',
  };
  return (
    <div className={`border rounded-lg p-4 flex flex-col gap-2 transition-all duration-200 hover:-translate-y-1 hover:shadow-md ${colors[accent] || 'bg-slate-50 border-slate-200'}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs tracking-widest uppercase font-semibold opacity-60">{label}</span>
        {Icon && <Icon className="w-4 h-4 opacity-40" />}
      </div>
      <p className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
      {subtitle && <p className="text-xs opacity-60">{subtitle}</p>}
    </div>
  );
}

function OperatorRow({ op, rank, maxWon }) {
  const progress = maxWon > 0 ? (op.won / maxWon) * 100 : 0;
  return (
    <div className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0" data-testid={`operator-rank-${op.id}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
        rank === 1 ? 'bg-amber-400 text-white' :
        rank === 2 ? 'bg-slate-300 text-white' :
        rank === 3 ? 'bg-orange-300 text-white' :
        'bg-slate-100 text-slate-500'
      }`}>
        {rank}
      </div>
      <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-sm font-bold shrink-0">
        {op.name.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-slate-900 truncate">{op.name}</p>
          {op.paused && (
            <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0">
              <Pause className="w-2.5 h-2.5 mr-0.5" />PAUSADO
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-4 mt-1">
          <Progress value={progress} className="flex-1 h-1.5" />
          <div className="flex items-center gap-3 text-xs text-slate-500 shrink-0">
            <span>{op.total_leads} leads</span>
            <span className="text-emerald-600 font-semibold">{op.won} vendas</span>
            <span>{op.conversion}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function TeamCard({ team, detailed }) {
  const maxWon = Math.max(...(team.operators || []).map(o => o.won), 1);
  return (
    <Card className="p-6" data-testid={`team-card-${team.team_id}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {team.team_name}
          </h3>
          <p className="text-sm text-slate-500">Supervisor: {team.supervisor_name}</p>
        </div>
        <Badge variant="outline" className="text-sm">
          {team.operator_count || team.operators?.length || 0} operadores
        </Badge>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <MetricCard label="Total Leads" value={team.total_leads} icon={Users} accent="blue" />
        <MetricCard label="Vendas" value={team.won} icon={Trophy} accent="emerald" />
        <MetricCard label="Em Andamento" value={team.active} icon={Target} accent="amber" />
        <MetricCard label="Conversao" value={`${team.conversion}%`} icon={TrendingUp} accent={team.conversion >= 10 ? 'emerald' : 'red'} />
      </div>
      {detailed && team.operators && team.operators.length > 0 && (
        <div>
          <p className="text-xs tracking-widest uppercase font-semibold text-slate-400 mb-3">Ranking de Operadores</p>
          {team.operators.map((op, i) => (
            <OperatorRow key={op.id} op={op} rank={i + 1} maxWon={maxWon} />
          ))}
        </div>
      )}
    </Card>
  );
}

export default function PerformancePage() {
  const { user } = useAuth();
  const [period, setPeriod] = useState('month');
  const [teams, setTeams] = useState([]);
  const [teamDetails, setTeamDetails] = useState({});
  const [loading, setLoading] = useState(true);

  // Distribution report state
  const [reportPeriod, setReportPeriod] = useState('month');
  const [reportProduct, setReportProduct] = useState('all');
  const [reportTeam, setReportTeam] = useState('all');
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [allTeams, setAllTeams] = useState([]);

  const isAdmin = user?.role === 'admin';
  const isSupervisor = user?.role === 'supervisor';
  const canSeeAllTeams = isAdmin; // Maikon, Juliana, Bruno veem todas equipes

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const compRes = await api.getTeamsComparison(period);
      setTeams(compRes.data.teams || []);

      const teamsRes = await api.getTeams();
      setAllTeams(teamsRes.data);
      const details = {};
      for (const t of teamsRes.data) {
        try {
          const perfRes = await api.getTeamPerformance(t.id, period);
          details[t.id] = perfRes.data;
        } catch {}
      }
      setTeamDetails(details);
    } catch {}
    setLoading(false);
  }, [period]);

  const fetchReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const res = await api.getDistributionReport({
        period: reportPeriod,
        product: reportProduct,
        team_id: reportTeam
      });
      setReport(res.data);
    } catch {}
    setReportLoading(false);
  }, [reportPeriod, reportProduct, reportTeam]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchReport(); }, [fetchReport]);

  const totalLeadsAll = teams.reduce((s, t) => s + t.total_leads, 0);
  const totalWonAll = teams.reduce((s, t) => s + t.won, 0);
  const overallConversion = totalLeadsAll > 0 ? ((totalWonAll / totalLeadsAll) * 100).toFixed(1) : 0;
  const bestConversion = teams.length > 0 ? Math.max(...teams.map(t => t.conversion)) : 0;

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <Sidebar />
      <main className="flex-1 p-8">
        <div className="max-w-[1600px] mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Performance
              </h1>
              <p className="text-slate-500 mt-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Metricas de desempenho por equipe
              </p>
            </div>
            <Tabs value={period} onValueChange={setPeriod} data-testid="period-filter-tabs">
              <TabsList>
                <TabsTrigger value="today" data-testid="period-today">Hoje</TabsTrigger>
                <TabsTrigger value="week" data-testid="period-week">Semana</TabsTrigger>
                <TabsTrigger value="month" data-testid="period-month">Mes</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Overall Summary (Admin only) */}
              {canSeeAllTeams && teams.length > 1 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                  <MetricCard label="Total Geral" value={totalLeadsAll} subtitle="Todas as equipes" icon={Users} accent="blue" />
                  <MetricCard label="Vendas Totais" value={totalWonAll} subtitle="Todas as equipes" icon={Trophy} accent="emerald" />
                  <MetricCard label="Conversao Geral" value={`${overallConversion}%`} subtitle="Media geral" icon={TrendingUp} accent={overallConversion >= 10 ? 'emerald' : 'amber'} />
                </div>
              )}

              {/* Team Comparison Bar (Admin only) */}
              {canSeeAllTeams && teams.length > 1 && (
                <Card className="p-6 mb-8" data-testid="team-comparison-bar">
                  <p className="text-xs tracking-widest uppercase font-semibold text-slate-400 mb-4">Comparativo de Equipes</p>
                  <div className="space-y-4">
                    {teams.map((t) => {
                      const pct = totalLeadsAll > 0 ? ((t.total_leads / totalLeadsAll) * 100) : 0;
                      const isTop = t.conversion === bestConversion && teams.length > 1;
                      return (
                        <div key={t.team_id} className="flex items-center gap-4">
                          <div className="w-36 shrink-0">
                            <p className="text-sm font-medium text-slate-900">{t.team_name}</p>
                            <p className="text-xs text-slate-400">{t.operator_count} ops</p>
                          </div>
                          <div className="flex-1"><Progress value={pct} className="h-3" /></div>
                          <div className="flex items-center gap-3 shrink-0 w-48 justify-end">
                            <span className="text-sm text-slate-600">{t.total_leads} leads</span>
                            <span className="text-sm font-semibold text-emerald-600">{t.won} vendas</span>
                            <Badge className={isTop ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                              {t.conversion}%{isTop && <ArrowUp className="w-3 h-3 ml-0.5" />}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}

              {/* Team Detail Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start mb-8">
                {teams.map((t) => {
                  const detail = teamDetails[t.team_id] || t;
                  return <TeamCard key={t.team_id} team={detail} detailed />;
                })}
              </div>

              {teams.length === 0 && (
                <Card className="p-12 text-center mb-8">
                  <p className="text-slate-400">Nenhuma equipe configurada ainda. Adicione operadores as equipes para ver metricas.</p>
                </Card>
              )}

              {/* ========== DISTRIBUTION REPORT ========== */}
              <Card className="p-6" data-testid="distribution-report">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Distribuicao de Leads
                      </h2>
                      <p className="text-xs text-slate-500">Relatorio detalhado por operador</p>
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="flex items-center gap-3">
                    <Select value={reportPeriod} onValueChange={setReportPeriod}>
                      <SelectTrigger className="w-32 h-9 text-xs" data-testid="report-period-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="today">Hoje</SelectItem>
                        <SelectItem value="week">7 dias</SelectItem>
                        <SelectItem value="custom_15">15 dias</SelectItem>
                        <SelectItem value="month">30 dias</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={reportProduct} onValueChange={setReportProduct}>
                      <SelectTrigger className="w-32 h-9 text-xs" data-testid="report-product-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos produtos</SelectItem>
                        <SelectItem value="serum">Serum Facial</SelectItem>
                        <SelectItem value="dores">Produto Dores</SelectItem>
                      </SelectContent>
                    </Select>

                    {canSeeAllTeams && (
                      <Select value={reportTeam} onValueChange={setReportTeam}>
                        <SelectTrigger className="w-36 h-9 text-xs" data-testid="report-team-filter">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas equipes</SelectItem>
                          {allTeams.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>

                {/* Summary bar */}
                {report && (
                  <div className="flex items-center gap-6 mb-4 p-3 bg-slate-50 rounded-lg">
                    <div className="text-sm">
                      <span className="text-slate-500">Total de leads: </span>
                      <span className="font-semibold text-slate-900">{report.total_leads}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-slate-500">Media por operador: </span>
                      <span className="font-semibold text-slate-900">{report.average_per_operator}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-slate-500">Operadores: </span>
                      <span className="font-semibold text-slate-900">{report.operators?.length || 0}</span>
                    </div>
                  </div>
                )}

                {reportLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-6 h-6 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                  </div>
                ) : report && report.operators?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/50">
                          <TableHead className="w-8">#</TableHead>
                          <TableHead>Operador</TableHead>
                          <TableHead>Equipe</TableHead>
                          <TableHead className="text-center">Recebidos</TableHead>
                          <TableHead className="text-center">Novos</TableHead>
                          <TableHead className="text-center">Contatados</TableHead>
                          <TableHead className="text-center">Negociando</TableHead>
                          <TableHead className="text-center">Vendas</TableHead>
                          <TableHead className="text-center">Perdidos</TableHead>
                          <TableHead className="text-center">Conversao</TableHead>
                          <TableHead className="w-32">Distribuicao</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.operators.map((op, i) => {
                          const deviation = report.average_per_operator > 0
                            ? ((op.total - report.average_per_operator) / report.average_per_operator * 100).toFixed(0)
                            : 0;
                          const isBelow = op.total < report.average_per_operator * 0.8;
                          const isAbove = op.total > report.average_per_operator * 1.2;
                          const barPct = report.total_leads > 0 ? (op.total / report.total_leads * 100) : 0;

                          return (
                            <TableRow key={op.id} data-testid={`report-row-${op.id}`} className={isBelow ? 'bg-red-50/30' : isAbove ? 'bg-emerald-50/30' : ''}>
                              <TableCell className="font-medium text-slate-400 text-xs">{i + 1}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                                    {op.name.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-slate-900">{op.name}</p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="text-xs text-slate-500">{op.team}</span>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className="text-sm font-semibold text-slate-900">{op.total}</span>
                                {isBelow && <AlertTriangle className="w-3 h-3 text-red-400 inline ml-1" />}
                              </TableCell>
                              <TableCell className="text-center text-sm text-blue-600">{op.new}</TableCell>
                              <TableCell className="text-center text-sm text-slate-600">{op.contacted}</TableCell>
                              <TableCell className="text-center text-sm text-amber-600">{op.negotiating}</TableCell>
                              <TableCell className="text-center">
                                <span className="text-sm font-semibold text-emerald-600">{op.won}</span>
                              </TableCell>
                              <TableCell className="text-center text-sm text-red-500">{op.lost}</TableCell>
                              <TableCell className="text-center">
                                <Badge className={op.conversion >= 10 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                                  {op.conversion}%
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Progress value={barPct} className="flex-1 h-2" />
                                  <span className={`text-[10px] font-medium ${isBelow ? 'text-red-500' : isAbove ? 'text-emerald-600' : 'text-slate-400'}`}>
                                    {deviation > 0 ? '+' : ''}{deviation}%
                                  </span>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    Nenhum dado encontrado para o periodo selecionado.
                  </div>
                )}
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
